import { AdminPagination as ZhPagination } from "@/views/components/AdminPagination";
import { Alert, Button, Card, Space, message } from "antd";
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import {
    BaseItem as ZhBaseItem,
    DetailHeader as ZhDetailHeader,
    Grid as ZhGrid,
    type TableColumnProps
} from "zhihao-ui-react";

import request from "@/api";
import { DEFAULT_VALUE } from "@/config";
import { ABLE } from "@/meta/common";
import { BANK_CERT, INVOICE, INVOICE_HEADER } from "@/meta/customer/customer";
import { CHANNEL, COMMUNICATE_MODULE, INTENTION } from "@zhihaoscm/meta";
import type { CustomerInfo } from "@zhihaoscm/entity";
import Permission from "@/views/components/Permission";
import { AdminDetailTable } from "@/views/components/DetailTable";
import { useUserStore } from "@/stores/react/authStore";
import { areaLib } from "@/utils/areaLib";
import {
    CustomerCommunicationPage,
    type CustomerCommunicationQuery,
    type CustomerCommunicationRecord,
    loadCustomerCommunicationRecords
} from "./CustomerCommunicationPage";
import {
    CustomerLogisticsPage,
    type CustomerLogisticsQuery,
    type CustomerLogisticsRecord,
    loadCustomerLogistics
} from "./CustomerLogisticsPage";
import {
    CustomerProfilePage,
    type CustomerProfileBasic,
    type CustomerProfileOverview,
    type CustomerProfileStats,
    loadCustomerProfileBasic,
    loadCustomerProfileOverview,
    loadCustomerProfileStats
} from "./CustomerProfilePage";
import { CustomerSubAccountPage } from "./CustomerSubAccountPage";
import "./CustomerInfoDetailPage.scss";

interface CustomerDetailCustomer {
    id: number;
    code?: string;
    mobile?: string;
    email?: string;
    nickName?: string;
    realName?: string;
    idNo?: string;
    createdTime?: string;
    updatedTime?: string;
    institutionName?: string;
    unifiedSocialCreditCode?: string;
    legalRepresentative?: string;
    memberLevel?: number | string;
    memberState?: number | string;
    memberActiveTime?: string;
    memberExpiryDate?: string;
    state?: number | string;
}

export interface CustomerInfoDetailRecord {
    customer: CustomerDetailCustomer;
    customerInfo?: CustomerInfo;
    institutionApply?: {
        institutionName?: string;
        unifiedSocialCreditCode?: string;
        legalRepresentative?: string;
    };
    isCancelInstitutionApply?: boolean;
    addresses?: Array<{
        id?: number;
        contacts?: string;
        institutionName?: string;
        cellphone?: string;
        areaCode?: string;
        landline?: string;
        zipCode?: string;
        detailAddress?: string;
        isDefault?: number;
    }>;
    banks?: Array<{
        bank?: { id?: number; name?: string; account?: string; bank?: string; type?: number };
        bankDict?: { bankName?: string };
    }>;
    invoiceHeaders?: Array<{
        id?: number;
        name?: string;
        headerType?: number;
        invoiceType?: number;
        taxpayerNo?: string;
        bank?: string;
        account?: string;
        mobile?: string | number;
        address?: string;
        isDefault?: number;
    }>;
}

export interface CustomerInfoDetailPageProps {
    userId?: number;
    loadDetail?: (id: number) => Promise<CustomerInfoDetailRecord>;
    cancelApply?: (id: number) => Promise<unknown>;
    loadLogistics?: (query: CustomerLogisticsQuery) => Promise<{ list?: CustomerLogisticsRecord[]; total?: number }>;
    loadCommunication?: (
        query: CustomerCommunicationQuery
    ) => Promise<{ list?: CustomerCommunicationRecord[]; total?: number }>;
    loadProfileBasic?: (params: { customerId: number }) => Promise<CustomerProfileBasic>;
    loadProfileOverview?: (params: {
        customerId: number;
        beginTime: string;
        endTime: string;
    }) => Promise<CustomerProfileOverview>;
    loadProfileStats?: (params: {
        customerId: number;
        beginTime: string;
        endTime: string;
    }) => Promise<CustomerProfileStats>;
    loadRechargeRecords?: (query: CustomerRechargeQuery) => Promise<{
        list?: CustomerRechargeRow[];
        total?: number;
    }>;
    loadRechargeAmount?: (customerId: number) => Promise<number>;
    canWriteCommunication?: boolean;
}

export interface CustomerRechargeQuery {
    page: number;
    size: number;
    customerId: number;
}

export interface CustomerRechargeRow {
    id: number | string;
    memberLevel?: number | string;
    memberDay?: number;
    openMode?: number | string;
    activationCode?: string;
    amount?: number;
    activationDate?: string;
    expireDate?: string;
    createdTime?: string;
}

const memberLevelLabels: Record<number, string> = { 1: "非会员", 2: "基础会员", 3: "高级会员", 4: "企业会员" };
const memberStateLabels: Record<number, string> = { 1: "正常", 2: "已过期" };
const userStateLabels: Record<number, string> = { 0: "已禁用", 1: "已启用", 2: "禁用", 3: "已注销" };

type CustomerDetailTab = "base" | "member" | "subAccount" | "logistics" | "communication" | "profile";

/** 旧 Vue infoDetailHeader.vue 的内嵌页签顺序。 */
const customerDetailTabs: Array<{ key: CustomerDetailTab; label: string }> = [
    { key: "base", label: "基本信息" },
    { key: "member", label: "会员信息" },
    { key: "subAccount", label: "子账号信息" },
    { key: "logistics", label: "物流信息" },
    { key: "communication", label: "用户沟通记录" },
    { key: "profile", label: "用户画像" }
];

const formatDate = (value?: string) => (value ? value.replace("T", " ").slice(0, 16) : DEFAULT_VALUE);

/** 旧 Vue 详情页对过长邮箱做截断，避免账号信息撑开字段网格。 */
const formatEmail = (value?: string) => {
    if (!value) return DEFAULT_VALUE;
    return value.length > 32 ? `${value.slice(0, 32)}...` : value;
};

/** 复用 Vue `/admin/customer/find/:id` 用户详情接口。 */
export async function loadCustomerDetail(id: number): Promise<CustomerInfoDetailRecord> {
    const response = await request.service.get(`/admin/customer/find/${id}`);
    return response.data;
}

/** 保留 Vue 解除机构认证接口。 */
export async function cancelCustomerInstitutionApply(id: number) {
    const response = await request.service.put(`/admin/customer/cancelApply/${id}`);
    return response.data;
}

/** 复用 Vue 会员信息组件的充值记录分页接口，详情页只追加当前用户过滤条件。 */
export async function loadCustomerRechargeRecords(query: CustomerRechargeQuery) {
    const response = await request.service.get("/admin/member-open-record/paging", { params: query });
    return response.data as { list?: CustomerRechargeRow[]; total?: number };
}

/** 复用 Vue membershipInfo.vue 的总计实收金额接口。 */
export async function loadCustomerRechargeAmount(customerId: number) {
    const response = await request.service.get(`/admin/member-open-record/amount?customerId=${customerId}`);
    return Number(response.data || 0);
}

const membershipDays = (activeTime?: string, expiryTime?: string, state?: number | string) => {
    if (!activeTime || !expiryTime || Number(state) === 2) return "--";
    const days = Math.max(0, Math.round((new Date(expiryTime).getTime() - new Date(activeTime).getTime()) / 86400000));
    return `${days} 天`;
};

const rechargeMemberLevelLabels: Record<number, string> = { 2: "基础会员", 3: "高级会员", 4: "企业会员" };
const rechargeOpenModeLabels: Record<number, string> = {
    1: "微信支付",
    2: "支付宝",
    3: "激活码",
    4: "APPLE PAY"
};

/** 充值记录金额与 Vue 页面保持两位小数展示。 */
const formatRechargeAmount = (value?: number) => (value == null ? DEFAULT_VALUE : Number(value).toFixed(2));

/** 会员详情中的充值记录面板，负责分页数据的异步状态与核心字段展示。 */
function CustomerRechargeRecords({
    customerId,
    loadRecords,
    loadAmount
}: {
    customerId: number;
    loadRecords: (query: CustomerRechargeQuery) => Promise<{
        list?: CustomerRechargeRow[];
        total?: number;
    }>;
    loadAmount: (customerId: number) => Promise<number>;
}) {
    const [records, setRecords] = useState<CustomerRechargeRow[]>([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [errorMessage, setErrorMessage] = useState("");
    const [amount, setAmount] = useState<number>();

    useEffect(() => {
        let disposed = false;
        setLoading(true);
        setErrorMessage("");

        // 与 Vue membershipInfo.vue 一致，查询参数必须带当前详情用户 customerId。
        void loadRecords({ page: 1, size: 10, customerId })
            .then(result => {
                if (disposed) return;
                setRecords(result.list || []);
                setTotal(result.total || 0);
            })
            .catch(() => {
                if (disposed) return;
                setRecords([]);
                setTotal(0);
                setErrorMessage("会员充值记录加载失败");
            })
            .finally(() => {
                if (!disposed) setLoading(false);
            });

        return () => {
            disposed = true;
        };
    }, [customerId, loadRecords]);

    useEffect(() => {
        let disposed = false;
        void loadAmount(customerId)
            .then(value => {
                if (!disposed) setAmount(value);
            })
            .catch(() => {
                if (!disposed) setAmount(0);
            });
        return () => {
            disposed = true;
        };
    }, [customerId, loadAmount]);

    // 用 zhihao-ui-react 列契约承接旧 Vue 会员充值记录的字段展示。
    const columns: TableColumnProps<CustomerRechargeRow>[] = [
        { key: "id", label: "编号", minWidth: 138, prop: "id" },
        {
            key: "memberLevel",
            label: "会员等级",
            minWidth: 150,
            prop: "memberLevel",
            render: ({ row }) => rechargeMemberLevelLabels[Number(row.memberLevel)] || DEFAULT_VALUE
        },
        {
            key: "openMode",
            label: "支付/兑换方式",
            minWidth: 165,
            prop: "openMode",
            render: ({ row }) => rechargeOpenModeLabels[Number(row.openMode)] || DEFAULT_VALUE
        },
        { key: "activationCode", label: "激活码", minWidth: 228, prop: "activationCode" },
        {
            key: "amount",
            label: "实收金额（元）",
            minWidth: 214,
            prop: "amount",
            render: ({ row }) => formatRechargeAmount(row.amount)
        },
        {
            key: "activationDate",
            label: "激活日期",
            minWidth: 120,
            prop: "activationDate",
            render: ({ row }) => formatDate(row.activationDate)
        },
        {
            key: "expireDate",
            label: "到期日期",
            minWidth: 110,
            prop: "expireDate",
            render: ({ row }) => formatDate(row.expireDate)
        }
    ];

    return (
        <section aria-label="customer-recharge-records" className="customer-recharge-records">
            <AdminDetailTable<CustomerRechargeRow>
                data={records}
                columns={columns}
                emptyText={loading ? "加载中..." : "暂无数据"}
                pagination={false}
                rowKey="id"
                isShowResetBtn={false}
                isShowSearchBtn={false}
                tableHeader={
                    <div className="admin-table-composed-header">
                        <h2 className="section-title">充值记录</h2>

                        <p>总计实收金额：{amount == null ? DEFAULT_VALUE : amount.toFixed(2)} 元</p>

                        {errorMessage ? (
                            <Alert className="mb-4" message={errorMessage} role="alert" type="error" />
                        ) : null}
                    </div>
                }
                append={
                    total > 0 ? (
                        <div className="admin-table-composed-pagination">
                            <ZhPagination current={1} pageSize={10} total={total} hideOnSinglePage />
                        </div>
                    ) : null
                }
            />
        </section>
    );
}

const display = (value?: string | number | null) =>
    value === undefined || value === null || value === "" ? DEFAULT_VALUE : String(value);

interface CustomerDetailCardItem {
    label: string;
    value: string;
}

/** 完整复刻旧 Vue InfoCard 的标题、默认标识和左右字段行。 */
function CustomerDetailInfoCard({
    title,
    isDefault,
    items
}: {
    title: string;
    isDefault?: boolean;
    items: CustomerDetailCardItem[];
}) {
    return (
        <Card
            className="admin-detail-info-card"
            extra={isDefault ? <span className="admin-detail-info-card__default">默认</span> : null}
            title={title}
        >
            {items.map(item => (
                <div className="admin-detail-info-card__row" key={item.label}>
                    <span>{item.label}</span>
                    <span>{item.value}</span>
                </div>
            ))}
        </Card>
    );
}

/** 旧版 baseInfo.vue 的关联卡片区，保留四档响应式列数和原始字段顺序。 */
function CustomerAssociatedInfo({ detail }: { detail: CustomerInfoDetailRecord }) {
    const addresses = detail.addresses || [];
    const banks = detail.banks || [];
    const invoiceHeaders = detail.invoiceHeaders || [];
    return (
        <section aria-label="customer-associated-info" className="customer-info-detail__associated">
            <h2 className="section-title">地址簿</h2>
            {addresses.length ? (
                <div className="admin-detail-card-grid">
                    {addresses.map(address => (
                        <CustomerDetailInfoCard
                            isDefault={Boolean(address.isDefault)}
                            items={[
                                { label: "机构名称", value: display(address.institutionName) },
                                { label: "手机号码", value: display(address.cellphone) },
                                {
                                    label: "固定电话",
                                    value: address.landline
                                        ? `${address.areaCode ? `${address.areaCode}-` : ""}${address.landline}`
                                        : DEFAULT_VALUE
                                },
                                { label: "邮政编码", value: display(address.zipCode) },
                                { label: "地址", value: display(address.detailAddress) }
                            ]}
                            key={address.id}
                            title={display(address.contacts)}
                        />
                    ))}
                </div>
            ) : (
                <div className="admin-detail-empty">暂无数据</div>
            )}
            <h2 className="section-title">银行账户</h2>
            {banks.length ? (
                <div className="admin-detail-card-grid">
                    {banks.map(bank => (
                        <CustomerDetailInfoCard
                            items={[
                                {
                                    label: "账户类型",
                                    value: BANK_CERT.getIntroByCode(bank.bank?.type) || DEFAULT_VALUE
                                },
                                { label: "开户名称", value: display(bank.bank?.name) },
                                { label: "银行账户", value: display(bank.bank?.account) },
                                { label: "银行", value: display(bank.bankDict?.bankName || bank.bank?.bank) }
                            ]}
                            key={bank.bank?.id}
                            title={display(bank.bank?.bank || bank.bankDict?.bankName)}
                        />
                    ))}
                </div>
            ) : (
                <div className="admin-detail-empty">暂无数据</div>
            )}
            <h2 className="section-title">发票抬头</h2>
            {invoiceHeaders.length ? (
                <div className="admin-detail-card-grid">
                    {invoiceHeaders.map(invoice => (
                        <CustomerDetailInfoCard
                            isDefault={Boolean(invoice.isDefault)}
                            items={[
                                {
                                    label: "抬头类型",
                                    value: INVOICE_HEADER.getIntroByCode(invoice.headerType) || DEFAULT_VALUE
                                },
                                {
                                    label: "发票类型",
                                    value: INVOICE.getIntroByCode(invoice.invoiceType) || DEFAULT_VALUE
                                },
                                { label: "纳税人识别号", value: display(invoice.taxpayerNo) },
                                { label: "开户行", value: display(invoice.bank) },
                                { label: "账号", value: display(invoice.account) },
                                { label: "电话", value: display(invoice.mobile) },
                                { label: "地址", value: display(invoice.address) }
                            ]}
                            key={invoice.id}
                            title={display(invoice.name)}
                        />
                    ))}
                </div>
            ) : (
                <div className="admin-detail-empty">暂无数据</div>
            )}
        </section>
    );
}

/** 迁移旧版 interactionRecord/userInfo/detail.vue，保留 zhihao-ui-react 网格字段契约。 */
function CustomerCommunicationUserInfo({ info }: { info?: CustomerInfo }) {
    const intention = info?.intention ? INTENTION.getIntroByCode(info.intention)?.slice(0, 1) : DEFAULT_VALUE;
    const yesNo = (value?: number) => (value == null ? DEFAULT_VALUE : value ? "是" : "否");
    const concernModule = info?.concernModule?.length
        ? info.concernModule.map(item => COMMUNICATE_MODULE.getIntroByCode(item)).join("、")
        : DEFAULT_VALUE;
    const userLabel = info?.userLabel?.length ? info.userLabel.join("、") : DEFAULT_VALUE;
    // 旧 Vue 通过 areaLib 展示省市名称，不能把行政区编码直接显示给用户。
    const region =
        areaLib.getFullAreaName(info?.provinceCode, info?.cityCode) ||
        (info?.provinceCode || info?.cityCode
            ? [info.provinceCode, info.cityCode].filter(Boolean).join("/")
            : DEFAULT_VALUE);
    return (
        <section
            aria-label="customer-communication-user-info"
            className="admin-detail-section customer-communication-user-info"
        >
            <h2 className="section-title">用户信息</h2>
            <ZhGrid cols={{ xs: 1, sm: 2, md: 2, lg: 3, xl: 4 }} gap={[20, 20]}>
                <ZhBaseItem label="姓名" value={display(info?.name)} />
                <ZhBaseItem label="区域" value={region} />
                <ZhBaseItem label="了解渠道" value={CHANNEL.getIntroByCode(info?.channel) || DEFAULT_VALUE} />
                <ZhBaseItem label="意向度" value={intention} />
                <ZhBaseItem label="是否为砂石行业" value={yesNo(info?.sandFlag)} />
                <ZhBaseItem label="行业" value={display(info?.industry)} />
                <ZhBaseItem label="是否需要入驻平台" value={yesNo(info?.settlePlatformFlag)} />
                <ZhBaseItem label="关注模块" value={concernModule} />
                <ZhBaseItem label="用户标签" value={userLabel} span={4} />
            </ZhGrid>
        </section>
    );
}

export function CustomerInfoDetailPage({
    userId,
    loadDetail = loadCustomerDetail,
    cancelApply = cancelCustomerInstitutionApply,
    loadLogistics = loadCustomerLogistics,
    loadCommunication = loadCustomerCommunicationRecords,
    loadProfileBasic = loadCustomerProfileBasic,
    loadProfileOverview = loadCustomerProfileOverview,
    loadProfileStats = loadCustomerProfileStats,
    loadRechargeRecords = loadCustomerRechargeRecords,
    loadRechargeAmount = loadCustomerRechargeAmount,
    canWriteCommunication = true
}: CustomerInfoDetailPageProps) {
    const location = useLocation();
    const navigate = useNavigate();
    const banButton = useUserStore(user => user.banButton);
    // Vue 详情页从 route.query.userId 取值；保留 prop 覆盖能力供嵌入式调用和测试使用。
    const routeUserId = new URLSearchParams(location.search).get("userId");
    const resolvedUserId =
        userId ?? (routeUserId && Number.isFinite(Number(routeUserId)) ? Number(routeUserId) : undefined);
    const [detail, setDetail] = useState<CustomerInfoDetailRecord>();
    const [activeTab, setActiveTab] = useState<CustomerDetailTab>("base");
    const [errorMessage, setErrorMessage] = useState("");

    const fetchDetail = () => {
        if (resolvedUserId == null) return Promise.resolve();
        return loadDetail(resolvedUserId)
            .then(result => {
                setDetail(result);
                setErrorMessage("");
            })
            .catch(() => setErrorMessage("用户详情加载失败"));
    };

    useEffect(() => {
        let disposed = false;
        if (resolvedUserId == null) return undefined;
        void loadDetail(resolvedUserId)
            .then(result => {
                if (!disposed) {
                    setDetail(result);
                    setErrorMessage("");
                }
            })
            .catch(() => {
                if (!disposed) setErrorMessage("用户详情加载失败");
            });
        return () => {
            disposed = true;
        };
    }, [loadDetail, resolvedUserId]);

    const customer = detail?.customer;
    const institutionName = customer?.institutionName || detail?.institutionApply?.institutionName;
    const creditCode = customer?.unifiedSocialCreditCode || detail?.institutionApply?.unifiedSocialCreditCode;
    const legalRepresentative = customer?.legalRepresentative || detail?.institutionApply?.legalRepresentative;

    const handleCancelApply = async () => {
        if (customer?.id == null) return;
        try {
            await cancelApply(customer.id);
            message.success("机构认证已解除");
            await fetchDetail();
        } catch {
            message.error("解除机构认证失败");
        }
    };

    return (
        <section
            aria-label="customer-info-detail-page"
            className="customer-info-detail admin-detail-page detail-page-transparent"
            data-active-tab={activeTab}
        >
            <div className="customer-info-detail__surface">
                <div className="customer-info-detail__header admin-detail-header-card">
                    <ZhDetailHeader
                        rightInfos={[{ label: "更新时间", value: formatDate(customer?.updatedTime) }]}
                        stateDesc={
                            ABLE.getIntroByCode(customer?.state) ||
                            userStateLabels[Number(customer?.state)] ||
                            DEFAULT_VALUE
                        }
                        title="用户详情"
                    />
                    {errorMessage ? <Alert className="mb-4" message={errorMessage} role="alert" type="error" /> : null}
                    <div aria-label="用户详情标签" className="admin-detail-tabs" role="tablist">
                        {customerDetailTabs.map(tab => (
                            <button
                                aria-pressed={activeTab === tab.key}
                                className={activeTab === tab.key ? "is-active" : undefined}
                                key={tab.key}
                                type="button"
                                onClick={() => setActiveTab(tab.key)}
                            >
                                {tab.label}
                            </button>
                        ))}
                    </div>
                </div>
                <div className="customer-info-detail__content">
                    {activeTab === "profile" && customer?.id != null ? (
                        <CustomerProfilePage
                            customerId={customer.id}
                            loadBasic={loadProfileBasic}
                            loadOverview={loadProfileOverview}
                            loadStats={loadProfileStats}
                        />
                    ) : activeTab === "communication" && customer?.id != null ? (
                        <>
                            <CustomerCommunicationUserInfo info={detail?.customerInfo} />
                            <CustomerCommunicationPage
                                customerId={customer.id}
                                canRead
                                canWrite={canWriteCommunication}
                                loadRecords={loadCommunication}
                            />
                        </>
                    ) : activeTab === "logistics" && customer?.id != null ? (
                        <CustomerLogisticsPage customerId={customer.id} loadLogistics={loadLogistics} />
                    ) : activeTab === "subAccount" && customer?.id != null ? (
                        <CustomerSubAccountPage mainAccountId={customer.id} />
                    ) : activeTab === "base" ? (
                        <section className="admin-detail-section" aria-label="用户基本信息">
                            {/* 每个标题和字段网格对应 Vue detailJson 的一个 block；block 之间只保留页面灰色间距，不额外绘制卡片。 */}
                            <div className="customer-info-detail__block">
                                <h2 className="section-title">账号信息</h2>
                                <ZhGrid cols={{ xs: 1, sm: 2, md: 2, lg: 3, xl: 4 }} gap={[12, 12]}>
                                    <ZhBaseItem label="用户ID" value={display(customer?.code)} />
                                    <ZhBaseItem label="注册手机号" value={display(customer?.mobile)} />
                                    <ZhBaseItem label="注册时间" value={formatDate(customer?.createdTime)} />
                                    <ZhBaseItem
                                        label="绑定微信"
                                        value={display(
                                            customer?.nickName === customer?.mobile ? undefined : customer?.nickName
                                        )}
                                    />
                                    <ZhBaseItem label="绑定邮箱" value={formatEmail(customer?.email)} />
                                </ZhGrid>
                            </div>
                            <div className="customer-info-detail__block">
                                <h2 className="section-title">个人认证信息</h2>
                                <ZhGrid cols={{ xs: 1, sm: 2, md: 2, lg: 3, xl: 4 }} gap={[12, 12]}>
                                    <ZhBaseItem label="个人实名" value={display(customer?.realName)} />
                                    <ZhBaseItem label="身份证号" value={customer?.idNo || "未认证"} />
                                </ZhGrid>
                            </div>
                            <div className="customer-info-detail__block">
                                <h2 className="section-title">组织机构信息</h2>
                                <ZhGrid cols={{ xs: 1, sm: 2, md: 2, lg: 3, xl: 4 }} gap={[12, 12]}>
                                    <ZhBaseItem label="组织机构名称" value={display(institutionName)} />
                                    <ZhBaseItem label="统一社会信用代码" value={creditCode || "未认证"} />
                                    <ZhBaseItem label="法人代表" value={legalRepresentative || "未认证"} />
                                </ZhGrid>
                            </div>
                            {detail ? <CustomerAssociatedInfo detail={detail} /> : null}
                        </section>
                    ) : (
                        <section className="admin-detail-section customer-member-section" aria-label="用户会员信息">
                            <div className="customer-info-detail__block">
                                <h2 className="section-title">会员信息</h2>
                                <ZhGrid
                                    className="customer-member-info-grid"
                                    cols={{ xs: 1, sm: 2, md: 2, lg: 3, xl: 4 }}
                                    gap={[12, 12]}
                                >
                                    <ZhBaseItem
                                        colon={false}
                                        label="等级"
                                        labelWidth="auto"
                                        value={memberLevelLabels[Number(customer?.memberLevel)] || DEFAULT_VALUE}
                                    />
                                    <ZhBaseItem
                                        colon={false}
                                        label="状态"
                                        labelWidth="auto"
                                        span={3}
                                        value={memberStateLabels[Number(customer?.memberState)] || DEFAULT_VALUE}
                                    />
                                    <ZhBaseItem
                                        colon={false}
                                        label="时长"
                                        labelWidth="auto"
                                        value={membershipDays(
                                            customer?.memberActiveTime,
                                            customer?.memberExpiryDate,
                                            customer?.memberState
                                        )}
                                    />
                                    <ZhBaseItem
                                        colon={false}
                                        label="激活日期"
                                        labelWidth="auto"
                                        value={formatDate(customer?.memberActiveTime)}
                                    />
                                    <ZhBaseItem
                                        colon={false}
                                        label="到期时间"
                                        labelWidth="auto"
                                        value={formatDate(customer?.memberExpiryDate)}
                                    />
                                </ZhGrid>
                            </div>
                            {customer?.id != null ? (
                                <CustomerRechargeRecords
                                    customerId={customer.id}
                                    loadRecords={loadRechargeRecords}
                                    loadAmount={loadRechargeAmount}
                                />
                            ) : (
                                <p>充值记录：{DEFAULT_VALUE}</p>
                            )}
                        </section>
                    )}
                </div>
            </div>
            <Space className="operation-fixed-btn" size={0}>
                {/* 对齐旧版 infoDetail 的固定返回操作，回到进入详情前的历史页面。 */}
                <Button aria-label="返回" onClick={() => navigate(-1)}>
                    返回
                </Button>
                {detail?.isCancelInstitutionApply ? (
                    <Permission anyOf={["ROLE_SS_CUSTOMER_W"]}>
                        <Button disabled={banButton} onClick={() => void handleCancelApply()}>
                            解除机构认证
                        </Button>
                    </Permission>
                ) : null}
            </Space>
        </section>
    );
}
